import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PuzzleMark } from "@/components/puzzle/PuzzleMark";

/**
 * Preloader — cinematic 4-piece assembly. Max 2.5s, force-show 3s fallback.
 * Shows once per session (sessionStorage gated). Toggle via ?preloader=force.
 */
const SESSION_KEY = "ko_preloader_shown_v1";

const shouldShow = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("preloader") === "force") return true;
    if (params.get("preloader") === "skip") return false;
    return sessionStorage.getItem(SESSION_KEY) !== "1";
  } catch {
    return true;
  }
};

const Preloader = () => {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return shouldShow();
  });
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!show) return;
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {}
    // Stage 1: pieces assemble (~1.2s)
    const t1 = setTimeout(() => setPhase(1), 1200);
    // Stage 2: text reveal (~0.6s after assemble)
    const t2 = setTimeout(() => setPhase(2), 1700);
    // Exit at ~2.4s
    const t3 = setTimeout(() => setShow(false), 2400);
    // Force-show fallback @ 3000ms
    const forceShow = setTimeout(() => setShow(false), 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(forceShow);
    };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="preloader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.6, ease: "easeInOut" } }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: "#0A0A0A" }}
        >
          {/* registration corner marks */}
          <div className="absolute inset-6 pointer-events-none">
            <span
              className="absolute top-0 left-0 w-3 h-px"
              style={{ background: "#0072CE" }}
            />
            <span
              className="absolute top-0 left-0 w-px h-3"
              style={{ background: "#0072CE" }}
            />
            <span
              className="absolute top-0 right-0 w-3 h-px"
              style={{ background: "#D6007F" }}
            />
            <span
              className="absolute top-0 right-0 w-px h-3"
              style={{ background: "#D6007F" }}
            />
            <span
              className="absolute bottom-0 left-0 w-3 h-px"
              style={{ background: "#FFD200" }}
            />
            <span
              className="absolute bottom-0 left-0 w-px h-3"
              style={{ background: "#FFD200" }}
            />
            <span
              className="absolute bottom-0 right-0 w-3 h-px"
              style={{ background: "#B89B5E" }}
            />
            <span
              className="absolute bottom-0 right-0 w-px h-3"
              style={{ background: "#B89B5E" }}
            />
          </div>

          <div className="flex flex-col items-center gap-8 px-6 text-center">
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <PuzzleMark size={84} animated />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: phase >= 1 ? 1 : 0, y: phase >= 1 ? 0 : 10 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="flex flex-col items-center gap-3"
            >
              <div
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 900,
                  letterSpacing: "0.18em",
                  fontSize: "clamp(18px, 3.6vw, 28px)",
                  color: "#F5F1E8",
                }}
              >
                KRISTER OVERSEAS
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: phase >= 2 ? 1 : 0 }}
                transition={{ duration: 0.5 }}
                className="ko-overline"
                style={{ color: "#B89B5E" }}
              >
                From Print to Presence. From Products to Identity.
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
